import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';

const apiUrl = process.env.REACT_APP_BASIX_API || '';

// item 861 — LIVE RNWY system capacity (Ryan's rnwy-system status API, proxied by our
// backend so the keys never reach the browser). ONE fleet query powers every surface:
// project-header chips, sidebar health dots, the venue-builder strip and the fleet page.

export interface RnwyFleetRow {
  projectId: string; // the BASIX project id
  projectName: string;
  customerName: string;
  rnwySystemId: string; // the deployed system slug (e.g. "twg-fontainebleau")
  ok: boolean; // false = the status API didn't answer for this system
  venuesRunning: number;
  venueCapacity: number; // REMAINING headroom (total = running + capacity)
  venueTotalCapacity: number;
}

export interface RnwyFleet {
  enabled: boolean;
  rows: RnwyFleetRow[];
}

// headroom → traffic light (Chad-approved thresholds: amber ≤5, red ≤2 or none)
export type RnwyHealth = 'green' | 'amber' | 'red';
export const rnwyHealth = (headroom: number): RnwyHealth => {
  if (headroom <= 2) return 'red';
  if (headroom <= 5) return 'amber';
  return 'green';
};

// item 1124 — a project can link MORE THAN ONE processor: the fleet feed now carries one
// row per (project, system). Single-chip surfaces aggregate with this helper — sums cover
// only the boxes that answered, and ok is true only when EVERY linked box answered (a
// partial sum shown as authoritative would understate capacity).
export interface RnwyProjectCapacity {
  rows: RnwyFleetRow[];
  systems: string[];
  ok: boolean;
  venuesRunning: number;
  venueCapacity: number;
  venueTotalCapacity: number;
}

export const rnwyForProject = (
  fleet: RnwyFleet | undefined,
  projectId?: string | null,
): RnwyProjectCapacity | null => {
  const rows = (fleet?.rows ?? []).filter((r) => r.projectId === projectId);
  if (rows.length === 0) return null;
  return {
    rows,
    systems: rows.map((r) => r.rnwySystemId),
    ok: rows.every((r) => r.ok),
    venuesRunning: rows.reduce((s, r) => s + (r.ok ? r.venuesRunning : 0), 0),
    venueCapacity: rows.reduce((s, r) => s + (r.ok ? r.venueCapacity : 0), 0),
    venueTotalCapacity: rows.reduce((s, r) => s + (r.ok ? r.venueTotalCapacity : 0), 0),
  };
};

export const useRnwyFleet = () => useQuery(
  'rnwyFleet',
  async (): Promise<RnwyFleet> => {
    const { data } = await axios.get(`${apiUrl}rnwy-status/fleet`);
    return data;
  },
  { staleTime: 60_000, refetchInterval: 120_000 },
);

// the deployed-systems list (the admin "Link RNWY system" picker)
export const useRnwySystems = (open: boolean) => useQuery(
  'rnwySystems',
  async (): Promise<{ enabled: boolean; systems: string[] }> => {
    const { data } = await axios.get(`${apiUrl}rnwy-status/systems`);
    return data;
  },
  { enabled: open, staleTime: 300_000 },
);

export const useRnwyLink = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ projectId, rnwySystemId }: { projectId: string; rnwySystemId: string | null }) => axios.post(
      `${apiUrl}rnwy-status/link/${projectId}`,
      { rnwySystemId },
    ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('rnwyFleet');
        queryClient.invalidateQueries('project');
      },
    },
  );
};
